<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import XlAvatar from '../xl/XlAvatar.vue';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlEmptyState from '../xl/XlEmptyState.vue';
import MessageList from './MessageList.vue';
import MessageComposer from './MessageComposer.vue';

const props = defineProps({
  policyholder: { type: Object, default: null },
  messages: { type: Array, default: () => [] },
  rules: { type: Object, default: undefined },
  loading: { type: Boolean, default: false },
  submit: { type: Function, required: true },
  error: { type: String, default: '' },
  unavailable: { type: String, default: '' },
  // The owning broker's name is noise on a broker's own threads.
  showBroker: { type: Boolean, default: false },
  agencyName: { type: String, default: 'the agency' },
});

const emit = defineEmits(['back', 'call', 'home', 'join', 'react', 'retry']);

const UNAVAILABLE_COPY = {
  missing: {
    headline: "This conversation isn't yours",
    body: "It isn't with one of your policyholders, or it no longer exists.",
  },
  forbidden: {
    headline: "You don't have access to messages",
    body: "Your role in this hub doesn't include policyholders. Ask the hub owner if you need it.",
  },
  failed: {
    headline: "Couldn't load this conversation",
    body: 'Something went wrong on our side. Try again in a moment.',
  },
};

const firstName = computed(() => (props.policyholder?.name || '').trim().split(/\s+/)[0] || 'them');

/**
 * What a broker needs before typing: cover held, when it next needs
 * attention, and whose customer this is. A part SSO could not answer is
 * dropped rather than shown empty.
 */
const context = computed(() => {
  const p = props.policyholder;
  if (!p) return '';

  const parts = [];

  if (p.policy_count !== null && p.policy_count !== undefined) {
    parts.push(`${p.policy_count} ${p.policy_count === 1 ? 'policy' : 'policies'}`);
  }

  if (typeof p.renews_in_days === 'number') {
    parts.push(renewal(p.renews_in_days));
  }

  if (props.showBroker && p.broker_name) parts.push(p.broker_name);

  return parts.join(' · ');
});

const renewal = (days) => {
  if (days < 0) return 'renewal passed';
  if (days === 0) return 'renews today';

  return `renews in ${days} ${days === 1 ? 'day' : 'days'}`;
};

const hasBroker = computed(() => props.policyholder?.has_broker !== false);

const placeholder = computed(() => {
  if (!props.policyholder) return 'Write a message…';

  return props.messages.length ? `Reply to ${firstName.value}…` : `Write to ${firstName.value}…`;
});

const scroller = ref(null);
let following = true;

const onScroll = () => {
  const el = scroller.value;
  following = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
};

// A thread opens at its latest message and follows new ones, unless the reader
// has scrolled up into the history.
watch(() => props.messages.length, async (length, previous) => {
  if (previous && !following) return;
  await nextTick();
  if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight;
});

const send = (message) => {
  following = true;

  return props.submit(message);
};
</script>

<template>
  <div class="thread" data-testid="conversation-thread">
    <XlButton v-if="props.unavailable !== 'forbidden'" variant="ghost" size="sm" class="thread__back" data-testid="thread-back" @click="emit('back')">
      <template #icon>
        <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M12.5 4.5L7 10l5.5 5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </template>
      All messages
    </XlButton>

    <XlCard v-if="props.unavailable">
      <XlEmptyState
        :headline="UNAVAILABLE_COPY[props.unavailable].headline"
        :body="UNAVAILABLE_COPY[props.unavailable].body"
        :data-state="props.unavailable"
      >
        <XlButton v-if="props.unavailable === 'failed'" @click="emit('retry')">Try again</XlButton>
        <XlButton v-if="props.unavailable === 'forbidden'" @click="emit('home')">Back to Overview</XlButton>
      </XlEmptyState>
    </XlCard>

    <template v-else>
      <XlCard v-if="props.policyholder" padded class="thread__who" data-testid="thread-header">
        <a
          class="thread__who-link"
          :href="`/policyholders/${props.policyholder.id}`"
          target="_blank"
          rel="noopener"
          data-testid="thread-record-link"
        >
          <XlAvatar :name="props.policyholder.name || ''" :src="props.policyholder.photo || ''" :color="props.policyholder.avatar_color || ''" :size="50" aria-hidden="true" />
          <span class="thread__who-text">
            <b>{{ props.policyholder.name || 'Unnamed policyholder' }}</b>
            <span v-if="context" data-testid="thread-context">{{ context }}</span>
          </span>
        </a>
        <XlButton variant="ghost" aria-label="Start a voice call" data-testid="call-voice" @click="emit('call', 'voice')">
          <template #icon>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" /></svg>
          </template>
        </XlButton>
        <XlButton variant="ghost" aria-label="Start a video call" data-testid="call-video" @click="emit('call', 'video')">
          <template #icon>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="6" width="13" height="12" rx="2.5" stroke="currentColor" stroke-width="1.8" /><path d="m16 10.5 5-3v9l-5-3" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" /></svg>
          </template>
        </XlButton>
      </XlCard>

      <div ref="scroller" class="thread__scroll" data-testid="thread-scroll" @scroll="onScroll">
        <p v-if="props.loading && !props.messages.length" class="thread__state">Loading the conversation…</p>
        <p v-else-if="!props.messages.length" class="thread__state thread__state--empty" data-testid="thread-empty">
          Nothing has been said yet. Write the first message below.
        </p>
        <MessageList v-else class="thread__msgs" agency-side :messages="props.messages" :other-name="firstName" :agency-name="props.agencyName" @join="(call) => emit('join', call)" @react="(r) => emit('react', r)" />
      </div>

      <p v-if="!hasBroker" class="thread__state" data-testid="thread-no-broker">
        {{ firstName }} has no broker yet. They'll see what you write here and can reply to it.
      </p>
      <MessageComposer
        :placeholder="placeholder"
        :rules="props.rules"
        :submit="send"
      />
    </template>

    <p v-if="props.error" role="alert" class="thread__error" data-testid="thread-error">{{ props.error }}</p>
  </div>
</template>

<style scoped lang="scss">
.thread {
  display: flex;
  flex-direction: column;
  // 64px is the page header above .page-content.
  height: calc(100dvh - 64px - var(--setup-page-padding) - 24px);
  min-height: 420px;

  &__back { align-self: flex-start; margin-bottom: 18px; }

  &__scroll {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }

  &__who {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 18px;

    :deep(.xl-avatar) { font-size: 16px !important; }
  }

  &__who-link {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
    margin-right: auto;
    color: inherit;
    text-decoration: none;
    border-radius: 6px;

    :deep(.xl-avatar) { transition: box-shadow 0.15s; }

    &:hover b { text-decoration: underline; text-underline-offset: 3px; }
    &:hover :deep(.xl-avatar) { box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--xl-color-primary); }
    &:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 4px; }
  }

  &__who-text {
    min-width: 0;

    b { font-size: 17px; font-weight: 700; color: var(--xl-color-primary-text); }
    span { display: block; }
    span { font-size: 13.5px; color: var(--xl-color-outline); }
  }

  &__msgs { margin: 18px 0; }

  &__state {
    margin: 18px 0;
    text-align: center;
    font-size: 14px;
    color: var(--xl-color-outline);
  }

  &__state--empty { margin: auto 0; padding: 24px 0; }

  &__error {
    margin: 10px 4px 0;
    font-size: 13px;
    color: var(--xl-color-error, #a32020);
  }
}
</style>
